import { useEffect, useState } from "react";
import { Loader2, Save, Code2, AlertTriangle } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function ScriptsAdmin() {
  const [form, setForm] = useState({ head_script: "", footer_script: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/site-settings")
      .then(({ data }) => setForm(data))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/site-settings", form);
      setForm(data);
      toast.success("Saved. Reload any page to see it take effect.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-gray-500">Loading…</div>;

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-display text-4xl font-black">Header & Footer Scripts</h1>
          <p className="text-gray-500 mt-1">
            Paste any HTML/JS snippets (Google Analytics, GTM, Meta Pixel, Hotjar, Intercom, custom CSS…). They render on every page.
          </p>
        </div>
        <Button
          onClick={save}
          disabled={saving}
          data-testid="scripts-save-btn"
          className="h-11 rounded-full bg-[#FA003F] hover:bg-[#C40031] font-bold px-6"
        >
          <Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save"}
        </Button>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-2xl px-5 py-4 flex gap-3 mb-6">
        <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900">
          Scripts are injected into the DOM on every page load — so Analytics, Tag Manager, Pixels and chat widgets fire correctly.
          They appear under <span className="font-bold">Inspect&nbsp;Element</span>. For scripts to also appear in raw <span className="font-bold">View&nbsp;Source</span>, you'll need to redeploy after adding them once (Emergent Support can help with SSR).
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <div className="bg-white rounded-2xl border border-black/10 p-6">
          <div className="flex items-center gap-2 mb-3">
            <Code2 className="h-4 w-4 text-[#FA003F]" />
            <Label className="text-sm font-bold uppercase tracking-wider text-gray-500">Head script (inside &lt;head&gt;)</Label>
          </div>
          <Textarea
            data-testid="head-script-input"
            value={form.head_script}
            onChange={(e) => setForm((p) => ({ ...p, head_script: e.target.value }))}
            spellCheck={false}
            placeholder={"<!-- Example: Google Analytics -->\n<script async src=\"https://www.googletagmanager.com/gtag/js?id=G-XXXX\"></script>\n<script>\n  window.dataLayer = window.dataLayer || [];\n  function gtag(){dataLayer.push(arguments);}\n  gtag('js', new Date());\n  gtag('config', 'G-XXXX');\n</script>"}
            className="rounded-xl min-h-64 font-mono text-xs"
          />
        </div>

        <div className="bg-white rounded-2xl border border-black/10 p-6">
          <div className="flex items-center gap-2 mb-3">
            <Code2 className="h-4 w-4 text-[#FA003F]" />
            <Label className="text-sm font-bold uppercase tracking-wider text-gray-500">Footer script (end of &lt;body&gt;)</Label>
          </div>
          <Textarea
            data-testid="footer-script-input"
            value={form.footer_script}
            onChange={(e) => setForm((p) => ({ ...p, footer_script: e.target.value }))}
            spellCheck={false}
            placeholder={"<!-- Example: Meta Pixel or a live-chat widget -->\n<script>\n  console.log('footer script loaded');\n</script>"}
            className="rounded-xl min-h-64 font-mono text-xs"
          />
        </div>
      </div>
    </div>
  );
}
